import React, { useState, useEffect } from 'react';

// API base URL from environment variable, with fallback to localhost:4000 for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const ServerStats = ({ socket, connectionUrl }) => {
  const [apiStatus, setApiStatus] = useState({ status: 'checking', responseTime: null });
  const [socketStatus, setSocketStatus] = useState('checking');
  const [databaseStatus, setDatabaseStatus] = useState('checking');
  const [serverStats, setServerStats] = useState({ activeRooms: 0, totalPlayers: 0, totalBots: 0 });

  // Test REST API connectivity
  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    const testApi = async () => {
      let testUrl;
      if (connectionUrl === '/' || connectionUrl === '') {
        testUrl = isDevelopment ? `${API_BASE_URL}/test` : '/test';
      } else {
        const baseUrl = connectionUrl.endsWith('/') ? connectionUrl.slice(0, -1) : connectionUrl;
        testUrl = `${baseUrl}/test`;
      }
      
      try {
        const startTime = Date.now();
        const response = await fetch(testUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
        const endTime = Date.now();
        const responseTime = endTime - startTime;
        
        if (response.ok) {
          const data = await response.json();
          setApiStatus({ status: 'online', responseTime, data });
        } else {
          setApiStatus({ status: 'error', responseTime, error: `HTTP ${response.status}` });
        }
      } catch (error) {
        setApiStatus({ status: 'offline', responseTime: null, error: error.message });
      }
    };

    // Fetch stats function
    const fetchStats = async () => {
      let statsUrl;
      if (connectionUrl === '/' || connectionUrl === '') {
        statsUrl = isDevelopment ? `${API_BASE_URL}/stats` : '/stats';
      } else {
        const baseUrl = connectionUrl.endsWith('/') ? connectionUrl.slice(0, -1) : connectionUrl;
        statsUrl = `${baseUrl}/stats`;
      }
      
      try {
        const response = await fetch(statsUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.stats) {
            setServerStats({
              activeRooms: data.stats.activeRooms || 0,
              totalPlayers: data.stats.totalPlayers || 0,
              totalBots: data.stats.totalBots || 0
            });
            
            // Update database status
            if (data.stats.database) {
              const db = data.stats.database;
              if (!db.configured) {
                setDatabaseStatus('not_configured');
              } else if (db.connected) {
                setDatabaseStatus('online');
              } else {
                setDatabaseStatus('offline');
              }
            }
          }
        }
      } catch (error) {
        console.error('[ServerStats] Stats fetch error:', error);
      }
    };

    // Test immediately
    testApi();
    fetchStats();

    // Update every 30 seconds
    const interval = setInterval(() => {
      testApi();
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [connectionUrl]);

  // Monitor Socket.IO connection status
  useEffect(() => {
    let currentSocket = null;
    let statusInterval = null;
    let setupInterval = null;

    const updateSocketStatus = () => {
      const activeSocket = socket?.current;
      if (!activeSocket) {
        setSocketStatus('offline');
        return;
      }

      const isConnected = activeSocket.connected === true || 
                         (activeSocket.id !== undefined && activeSocket.id !== null && activeSocket.id !== '') ||
                         (activeSocket.io && activeSocket.io.readyState === 'open');
      
      if (isConnected) {
        setSocketStatus('online');
      } else {
        setSocketStatus('offline');
      }
    };

    const setupSocketListeners = () => {
      const activeSocket = socket?.current;
      if (!activeSocket) {
        return;
      }

      if (currentSocket === activeSocket) {
        return;
      }

      if (currentSocket && currentSocket !== activeSocket) {
        currentSocket.off('connect');
        currentSocket.off('disconnect');
        currentSocket.off('connect_error');
      }

      currentSocket = activeSocket;

      const handleConnect = () => {
        setSocketStatus('online');
      };

      const handleDisconnect = () => {
        setSocketStatus('offline');
      };

      const handleConnectError = () => {
        setSocketStatus('offline');
      };

      activeSocket.on('connect', handleConnect);
      activeSocket.on('disconnect', handleDisconnect);
      activeSocket.on('connect_error', handleConnectError);

      updateSocketStatus();
    };

    if (socket?.current) {
      setupSocketListeners();
    } else {
      setupInterval = setInterval(() => {
        if (socket?.current) {
          setupSocketListeners();
          if (setupInterval) {
            clearInterval(setupInterval);
            setupInterval = null;
          }
        }
      }, 500);
    }

    statusInterval = setInterval(() => {
      updateSocketStatus();
      if (socket?.current && currentSocket !== socket.current) {
        setupSocketListeners();
      }
    }, 2000);

    const initialCheckTimeout = setTimeout(updateSocketStatus, 100);
    const secondCheckTimeout = setTimeout(updateSocketStatus, 500);

    return () => {
      clearTimeout(initialCheckTimeout);
      clearTimeout(secondCheckTimeout);
      if (statusInterval) clearInterval(statusInterval);
      if (setupInterval) clearInterval(setupInterval);
      if (currentSocket) {
        currentSocket.off('connect');
        currentSocket.off('disconnect');
        currentSocket.off('connect_error');
      }
    };
  }, [socket]);

  const getDatabaseStatusText = (status) => {
    switch (status) {
      case 'online':
        return 'Connected';
      case 'offline':
        return 'Disconnected';
      case 'not_configured':
        return 'Not Configured';
      case 'checking':
        return 'Checking...';
      default:
        return status;
    }
  };

  return (
    <div className="server-stats-container">
      <div className="server-stats-row">
        <div className="server-stat-item">
          <span className="server-stat-emoji">🏠</span>
          <span>
            <strong className="server-stat-value">{serverStats.activeRooms}</strong> 
            <span className="server-stat-label">
              {serverStats.activeRooms === 1 ? 'room' : 'rooms'}
            </span>
          </span>
        </div>
        
        <div className="server-stat-item">
          <span className="server-stat-emoji">👥</span>
          <span>
            <strong className="server-stat-value">{serverStats.totalPlayers}</strong>
            <span className="server-stat-label">
              {serverStats.totalPlayers === 1 ? 'player' : 'players'}
            </span>
          </span>
        </div>
        
        {serverStats.totalBots > 0 && (
          <div className="server-stat-item">
            <span className="server-stat-emoji">🤖</span>
            <span>
              <strong className="server-stat-value">{serverStats.totalBots}</strong>
              <span className="server-stat-label">
                {serverStats.totalBots === 1 ? 'bot' : 'bots'}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Connection Details - Always visible */}
      <div className="server-status-indicators">
        <div className="server-status-indicator">
          <div className={`server-status-dot ${apiStatus.status}`}></div>
          <div className="server-status-text">
            <span className="server-status-label">Server:</span>
            <span className="server-status-value">{apiStatus.status === 'online' ? 'Connected' : apiStatus.status}</span>
            {apiStatus.responseTime && <span className="server-status-detail">({apiStatus.responseTime}ms)</span>}
          </div>
        </div>
        <div className="server-status-indicator">
          <div className={`server-status-dot ${socketStatus}`}></div>
          <div className="server-status-text">
            <span className="server-status-label">Real-time:</span>
            <span className="server-status-value">{socketStatus === 'online' ? 'Connected' : socketStatus}</span>
          </div>
        </div>
        <div className="server-status-indicator">
          <div className={`server-status-dot ${databaseStatus}`}></div>
          <div className="server-status-text">
            <span className="server-status-label">Database:</span>
            <span className="server-status-value">{getDatabaseStatusText(databaseStatus)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerStats;

